import axios from 'axios';
import { API_URL, TOKEN_KEY, UNAUTHORIZED_EVENT } from './config';
import {
    clearAuthStorage,
    getStoredRefreshToken,
    persistAuthResponse,
} from './session';

const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh'];

const client = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
});

let isRefreshing = false;
let refreshQueue = [];

function processRefreshQueue(error, token = null) {
    refreshQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    refreshQueue = [];
}

function rejectWithApiMessage(error) {
    const apiMessage = error.response?.data?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return Promise.reject(new Error(apiMessage));
    }

    if (error.message) {
        return Promise.reject(error);
    }

    return Promise.reject(new Error('Не удалось выполнить запрос'));
}

export async function refreshAccessToken() {
    const refreshToken = getStoredRefreshToken();
    const body = refreshToken ? { refresh_token: refreshToken } : {};

    const { data } = await axios.post(`${API_URL}/auth/refresh`, body, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
    });

    persistAuthResponse(data);
    return data;
}

function clearSessionAndNotify() {
    clearAuthStorage();
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
}

client.interceptors.request.use((config) => {
    if (!config.skipAuthHeader) {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    }
    return config;
});

client.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        const originalRequest = error.config || {};
        const requestPath = originalRequest.url || '';
        const isAuthRequest = AUTH_PATHS.some((path) => requestPath.includes(path));

        if (
            status === 401
            && !isAuthRequest
            && !originalRequest.skipAuthRefresh
            && !originalRequest._retry
        ) {
            const hasRefresh = Boolean(getStoredRefreshToken());

            if (!hasRefresh) {
                clearSessionAndNotify();
                return rejectWithApiMessage(error);
            }

            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    refreshQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return client(originalRequest);
                    })
                    .catch((queueError) => rejectWithApiMessage(queueError));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const data = await refreshAccessToken();
                processRefreshQueue(null, data.token);
                originalRequest.headers.Authorization = `Bearer ${data.token}`;
                return client(originalRequest);
            } catch (refreshError) {
                processRefreshQueue(refreshError, null);
                clearSessionAndNotify();
                return rejectWithApiMessage(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        if (status === 401 && isAuthRequest) {
            clearSessionAndNotify();
        }

        return rejectWithApiMessage(error);
    },
);

export default client;
